import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Button, Drawer, Tooltip, Row, Col, Modal, message, Table, Input } from "antd";
import { DeleteOutlined, EditFilled, ExclamationCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { Loading } from "../../components/loading";
import { TenantApps } from "./tenantApps";
import styles from "./tenants.module.css";
import $authHost from "../../http/auth";
import { getUser } from "../selectors";
import permissions from "../../constants/permissions";
import { CreateTenantModal } from "./createTenantModal";
import { normalize } from "../../utils/normalize";

const { Search } = Input;
const { confirm } = Modal;

interface Tenant {
  connectionString: string;
  id: string;
  name: string;
  email: string;
  createdOn: string | null;
  updatedOn: string | null;
}

export const Tenants = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [data, setData] = useState<Tenant[]>([]);
  const [filteredData, setFilteredData] = useState<Tenant[]>([]);
  const [search, setSearch] = useState("");
  const [, setError] = useState(null);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [tenantModal, setTenantModal] = useState(false);
  const [isConfigOpen, setIsConfigOpen] = useState<boolean>(false);
  const user = useSelector(getUser);

  const { t } = useTranslation("tenants");

  const userCanReadTenants = user.permissions.includes(permissions.MANAGE_TENANTS_READ);
  const userCanWriteTenants = user.permissions.includes(permissions.MANAGE_TENANTS_WRITE);
  const userCanDeleteTenants = user.permissions.includes(permissions.MANAGE_TENANTS_DELETE);

  const sortByName = (a: Tenant, b: Tenant) => a.name.localeCompare(b.name);
  const sortByEmail = (a: Tenant, b: Tenant) => a.email.localeCompare(b.email);

  const columns = [
    {
      title: t("name"),
      dataIndex: "name",
      key: "name",
      width: "20%",
      sorter: sortByName,
      render: (value: string) => <span className={styles.noWrap}>{value}</span>,
    },
    {
      title: t("id"),
      dataIndex: "id",
      key: "id",
      width: "15%",
      render: (id: string) => {
        return (
          <Tooltip title={id}>
            <span
              className={styles.idCell}
              onClick={() => {
                navigator.clipboard.writeText(id).then(() => message.success(t("idCopiedToClipboard")));
              }}
            >{`${id.slice(0, 8)}...${id.slice(-8)}`}</span>
          </Tooltip>
        );
      },
    },
    {
      title: t("email"),
      dataIndex: "email",
      key: "email",
      width: "35%",
      sorter: sortByEmail,
      render: (value: string) => <span className={styles.noWrap}>{value}</span>,
    },
    {
      title: t("actions"),
      dataIndex: "id",
      key: "actions",
      width: "30%",
      render: (id: string, record: any) => {
        return (
          <div className={styles.actionsContainer}>
            <Tooltip title={userCanWriteTenants ? t("editTenant") : t("permissionDenied")}>
              <Button
                className={styles.actionIcon}
                icon={<EditFilled />}
                onClick={() => showDrawerConfig(id)}
                disabled={!userCanWriteTenants}
                data-testid="editTenant"
              />
            </Tooltip>
            <Tooltip title={userCanDeleteTenants ? t("deleteTenant") : t("permissionDenied")}>
              <Button
                className={styles.actionIcon}
                icon={<DeleteOutlined />}
                onClick={() => {
                  showConfirm(record);
                }}
                disabled={!userCanDeleteTenants}
                data-testid="deleteTenant"
              />
            </Tooltip>
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    $authHost
      .get("/Tenants")
      .then((res) => {
        setTenants(res.data);
      })
      .catch((err) => {
        setError(err?.message);
      })
      .finally(() => {
        setPageLoading(false);
      });
  }, []);

  useEffect(() => {
    setData(tenants);
  }, [tenants]);

  useEffect(() => {
    if (!search) {
      setFilteredData(data);
      return;
    }

    const searchableFields = ["email", "name", "id"] as const;
    setFilteredData(
      data.filter((tenant) => searchableFields.some((field) => tenant[field].toLowerCase().includes(search)))
    );
  }, [data, search]);

  const closeTenantModal = () => {
    setTenantModal(false);
  };

  const showDrawerConfig = (id: string) => {
    setSelectedTenantId(id);
    setIsConfigOpen(true);
  };

  const onCloseConfigs = () => {
    setIsConfigOpen(false);
    setSelectedTenantId(null);
  };

  const deleteTenant = (id: string) => {
    setPageLoading(true);
    $authHost
      .delete(`/Tenants/${id}`)
      .then(() => {
        setTenants((previousItems: Tenant[]) => {
          return previousItems.filter((item: Tenant) => item.id !== id);
        });
        message.success(t("userRemoved"));
      })
      .catch((err) => {
        setError(err.message);
      })
      .finally(() => {
        setPageLoading(false);
      });
  };

  const showConfirm = (tenant: Tenant) => {
    confirm({
      title: t("doYouWantToDeleteUser", { name: tenant.name }),
      icon: <ExclamationCircleOutlined />,
      content: "",
      onOk() {
        deleteTenant(tenant.id);
      },
      onCancel() {
        console.log("Cancel", tenant);
      },
    });
  };

  const onTenantSubmit = (tenant: Tenant) => {
    setTenants([...tenants, tenant]);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(normalize(e.target.value));
  };

  return (
    <>
      <h1 className={styles.heading}>{t("tenantList")}</h1>
      <Row className={styles.searchRow}>
        {pageLoading && <Loading />}
        <p>
          {userCanWriteTenants ? (
            <Button
              type="primary"
              onClick={() => {
                setTenantModal(true);
              }}
            >
              {t("addTenant")}
            </Button>
          ) : (
            <Tooltip title={t("permissionDenied")}>
              <Button type="primary" disabled>
                {t("addTenant")}
              </Button>
            </Tooltip>
          )}
        </p>
        <Search onSearch={(value) => setSearch(normalize(value))} onChange={handleSearchChange} allowClear />
        <Drawer
          title={t("applications")}
          placement="right"
          onClose={onCloseConfigs}
          open={isConfigOpen}
          mask={true}
          width={window.innerWidth > 768 ? 700 : window.innerWidth}
          destroyOnClose={true}
        >
          <TenantApps tenant={tenants.find((tenant) => tenant.id === selectedTenantId)} />
        </Drawer>
      </Row>
      <Row>
        <Col xs={24}>
          {userCanReadTenants && <Table scroll={{ x: true }} columns={columns} dataSource={filteredData} rowKey="id" />}
        </Col>
      </Row>

      <CreateTenantModal open={tenantModal} onClose={closeTenantModal} onSubmit={onTenantSubmit} />
    </>
  );
};
